export type FieldRule =
    | { type: "required"; message?: string }
    | { type: "email"; message?: string }
    | { type: "url"; message?: string }
    | { type: "minLength"; value: number; message?: string }
    | { type: "maxLength"; value: number; message?: string }
    | { type: "exactLength"; value: number; message?: string }
    | { type: "min"; value: number; message?: string }
    | { type: "max"; value: number; message?: string }
    | { type: "step"; value: number; message?: string }
    | { type: "pattern"; value: RegExp; message?: string }
    | { type: "enum"; values: (string | number)[]; message?: string }
    | { type: "not"; values: (string | number)[]; message?: string }
    | { type: "match"; field: string; message?: string }
    | { type: "different"; field: string; message?: string }
    | { type: "listMin"; value: number; message?: string }
    | { type: "listMax"; value: number; message?: string }
    | { type: "listUnique"; message?: string }
    | { type: "dateMin"; value: string; message?: string }
    | { type: "dateMax"; value: string; message?: string }
    | { type: "contains"; value: string; message?: string }
    | { type: "notContains"; value: string; message?: string }
    | { type: "startsWith"; value: string; message?: string }
    | { type: "endsWith"; value: string; message?: string }
    | { type: "custom"; check: (value: any, values: any) => boolean | Promise<boolean>; message?: string }
    | { type: "unique"; endpoint: string; key: string; availableKey?: string; message?: string }
    | { type: "api"; endpoint: string; key: string; resultOk: (data: any) => boolean; message?: string }
    | { type: "filesizeMax"; value: number; message?: string }
    | { type: "filesizeMin"; value: number; message?: string }
    | { type: "mimetype"; types: string[]; message?: string }

const defaultMsg = {
    required: "Pflichtfeld",
    email: "Ungültige E-Mail-Adresse",
    url: "Ungültige URL",
    minLength: (n: number) => `Mindestens ${n} Zeichen`,
    maxLength: (n: number) => `Höchstens ${n} Zeichen`,
    exactLength: (n: number) => `Genau ${n} Zeichen erforderlich`,
    min: (n: number) => `Mindestens ${n}`,
    max: (n: number) => `Höchstens ${n}`,
    step: (n: number) => `Nur Schritte von ${n}`,
    pattern: "Ungültiges Format",
    enum: "Ungültige Auswahl",
    not: "Nicht erlaubt",
    match: "Stimmt nicht überein",
    different: "Darf nicht gleich sein",
    listMin: (n: number) => `Mindestens ${n} Einträge`,
    listMax: (n: number) => `Höchstens ${n} Einträge`,
    listUnique: "Duplikate nicht erlaubt",
    dateMin: (d: string) => `Nicht vor ${d}`,
    dateMax: (d: string) => `Nicht nach ${d}`,
    contains: (v: string) => `Muss "${v}" enthalten`,
    notContains: (v: string) => `Darf "${v}" nicht enthalten`,
    startsWith: (v: string) => `Muss mit "${v}" beginnen`,
    endsWith: (v: string) => `Muss mit "${v}" enden`,
    unique: "Bereits vergeben",
    api: "Ungültig",
    filesizeMax: (n: number) => `Max. Dateigröße ${n} Bytes`,
    filesizeMin: (n: number) => `Min. Dateigröße ${n} Bytes`,
    mimetype: (l: string[]) => `Erlaubt: ${l.join(", ")}`,
    custom: "Ungültig",
}

// Fix: Better isEmpty function that handles all field types correctly
export function isEmpty(v: unknown): boolean {
    if (v === null || v === undefined) return true
    if (typeof v === "string") return v.trim() === ""
    if (Array.isArray(v)) return v.length === 0
    if (typeof v === "boolean") return false // checkbox: false is valid
    if (typeof v === "number") return false // 0 is valid
    if (typeof v === "object" && v && "size" in v) return false // File
    return false
}

export function fieldErrorsToState(fe: Record<string, string>): Record<string, { message: string }> {
    const next: Record<string, { message: string }> = {}
    Object.keys(fe).forEach((k) => (next[k] = { message: fe[k] }))
    return next
}

export async function getFieldError(def: any, values: Record<string, unknown>): Promise<string> {
    if (!def) return ""

    // Fix: Use def.name or the key from the field definition
    const name = def.name || def.key
    if (!name) return ""

    const value = values[name]
    const rules: FieldRule[] = def.rules || []

    for (const rule of rules) {
        // Handle required validation first - special case for checkboxes
        if (rule.type === "required") {
            if (def.type === "checkbox") {
                // For checkboxes, required means it must be checked (true)
                if (value !== true) {
                    return rule.message || defaultMsg.required
                }
            } else {
                // For all other fields, required means not empty
                if (isEmpty(value)) {
                    return rule.message || defaultMsg.required
                }
            }
            // If required validation passes, continue with other validations
            continue
        }

        // Skip other validations if value is empty (but not for booleans)
        if (isEmpty(value) && typeof value !== "boolean") continue

        // Email validation
        if (rule.type === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(String(value))) {
                return rule.message || defaultMsg.email
            }
        }

        // URL validation
        if (rule.type === "url") {
            const urlRegex = /^https?:\/\/\S+\.\S+/
            if (!urlRegex.test(String(value))) {
                return rule.message || defaultMsg.url
            }
        }

        // Length validations
        if (rule.type === "minLength" && String(value).length < rule.value) {
            return rule.message || defaultMsg.minLength(rule.value)
        }

        if (rule.type === "maxLength" && String(value).length > rule.value) {
            return rule.message || defaultMsg.maxLength(rule.value)
        }

        if (rule.type === "exactLength" && String(value).length !== rule.value) {
            return rule.message || defaultMsg.exactLength(rule.value)
        }

        // Numeric validations
        if (rule.type === "min") {
            const numValue = Number(value)
            if (!isNaN(numValue) && numValue < rule.value) {
                return rule.message || defaultMsg.min(rule.value)
            }
        }

        if (rule.type === "max") {
            const numValue = Number(value)
            if (!isNaN(numValue) && numValue > rule.value) {
                return rule.message || defaultMsg.max(rule.value)
            }
        }

        if (rule.type === "step") {
            const numValue = Number(value)
            if (!isNaN(numValue) && (numValue % rule.value !== 0)) {
                return rule.message || defaultMsg.step(rule.value)
            }
        }

        // Pattern validation
        if (rule.type === "pattern" && !rule.value.test(String(value))) {
            return rule.message || defaultMsg.pattern
        }

        // Enum validation
        if (rule.type === "enum" && !rule.values.includes(value as string | number)) {
            return rule.message || defaultMsg.enum
        }

        if (rule.type === "not" && rule.values.includes(value as string | number)) {
            return rule.message || defaultMsg.not
        }

        // Field comparison validations
        if (rule.type === "match" && value !== values[rule.field]) {
            return rule.message || defaultMsg.match
        }

        if (rule.type === "different" && value === values[rule.field]) {
            return rule.message || defaultMsg.different
        }

        // Array validations
        if (rule.type === "listMin" && Array.isArray(value) && value.length < rule.value) {
            return rule.message || defaultMsg.listMin(rule.value)
        }

        if (rule.type === "listMax" && Array.isArray(value) && value.length > rule.value) {
            return rule.message || defaultMsg.listMax(rule.value)
        }

        if (rule.type === "listUnique" && Array.isArray(value) && new Set(value).size !== value.length) {
            return rule.message || defaultMsg.listUnique
        }

        // Date validations
        if (rule.type === "dateMin" && new Date(value as any) < new Date(rule.value)) {
            return rule.message || defaultMsg.dateMin(rule.value)
        }

        if (rule.type === "dateMax" && new Date(value as any) > new Date(rule.value)) {
            return rule.message || defaultMsg.dateMax(rule.value)
        }

        // String content validations
        if (rule.type === "contains" && !String(value).includes(rule.value)) {
            return rule.message || defaultMsg.contains(rule.value)
        }

        if (rule.type === "notContains" && String(value).includes(rule.value)) {
            return rule.message || defaultMsg.notContains(rule.value)
        }

        if (rule.type === "startsWith" && !String(value).startsWith(rule.value)) {
            return rule.message || defaultMsg.startsWith(rule.value)
        }

        if (rule.type === "endsWith" && !String(value).endsWith(rule.value)) {
            return rule.message || defaultMsg.endsWith(rule.value)
        }

        // File validations
        if (rule.type === "filesizeMax" && typeof value === "object" && value && "size" in value && (value as any).size > rule.value) {
            return rule.message || defaultMsg.filesizeMax(rule.value)
        }

        if (rule.type === "filesizeMin" && typeof value === "object" && value && "size" in value && (value as any).size < rule.value) {
            return rule.message || defaultMsg.filesizeMin(rule.value)
        }

        if (rule.type === "mimetype" && typeof value === "object" && value && "type" in value && !rule.types.includes((value as any).type)) {
            return rule.message || defaultMsg.mimetype(rule.types)
        }

        // Custom validation
        if (rule.type === "custom") {
            try {
                const ok = await rule.check(value, values)
                if (!ok) return rule.message || defaultMsg.custom
            } catch {
                return rule.message || defaultMsg.custom
            }
        }

        // API validations
        if (rule.type === "unique") {
            const payload: any = {}
            payload[rule.key] = value
            try {
                const res = await fetch(rule.endpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                })
                const data = await res.json()
                const ok = data?.[rule.availableKey || "available"]
                if (ok !== true) return rule.message || defaultMsg.unique
            } catch {
                return rule.message || defaultMsg.unique
            }
        }

        if (rule.type === "api") {
            const payload: any = {}
            payload[rule.key] = value
            try {
                const res = await fetch(rule.endpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                })
                const data = await res.json()
                const ok = rule.resultOk(data)
                if (!ok) return rule.message || defaultMsg.api
            } catch {
                return rule.message || defaultMsg.api
            }
        }
    }
    return ""
}

export async function getAllFieldErrors(fields: Record<string, any>, values: Record<string, unknown>) {
    const out: Record<string, string> = {}
    for (const key of Object.keys(fields)) {
        const msg = await getFieldError(fields[key], values)
        if (msg) out[key] = msg
    }
    return out
}
